// The federal audits and standards each flag cites. Transcribed from
// reference/corpus.md Part A. Every incident here has a named cause in a
// published report — nothing is inferred from a search snippet.

import type { FailureModeId } from '../engine/types';

export type EvidenceCaseId =
  | 'GSA-2020'
  | 'GSA-2025'
  | 'VA-2023'
  | 'FEMP-AUDIT-2024'
  | 'FEMP-MV-5';

export interface Incident {
  project: string;
  cause: string;
  /** As stated in the report. Omitted where the report attaches no figure. */
  amount?: string;
  failureModeIds: FailureModeId[];
}

export interface EvidenceCase {
  id: EvidenceCaseId;
  issuer: string;
  /** Null for standards and guidance, which carry a version rather than a number. */
  reportNumber: string | null;
  /**
   * How this case is named where several are listed together. It must identify
   * the issuer as well as the document: a bare report number beside a bare
   * issuer name reads as one citation with the wrong attribution.
   */
  shortLabel: string;
  date: string;
  url: string;
  /** Scale of the review, for the evidence strip. */
  scale: string;
  summary: string;
  incidents: Incident[];
}

export const EVIDENCE_CASES: Record<EvidenceCaseId, EvidenceCase> = {
  'GSA-2020': {
    id: 'GSA-2020',
    shortLabel: 'GSA OIG A180017',
    issuer: 'GSA Office of Inspector General',
    reportNumber: 'A180017/P/5/R20004',
    date: '2020-03-27',
    url: 'https://www.gsaig.gov/sites/default/files/audit-reports/A180017%20-%20PBS_s%20$1.7%20Billion%20Energy%20Savings%20Performance%20Contracts%20Are%20Not%20Achieving%20Energy%20and%20Cost%20Savings%20Due%20to%20Inadequat.pdf',
    scale: '7 ESPCs · $1.7B',
    summary:
      'Seven named ESPC task orders totalling $1.7B, with findings mapped project-by-project in Appendix B. The densest single source in the corpus.',
    incidents: [
      {
        project: 'White Oak, MD',
        cause:
          'No post-installation witnessing; no written Year 3 M&V review; commissioning report still not submitted 4+ years after acceptance.',
        amount: '$1.50B task order',
        failureModeIds: ['FM-02'],
      },
      {
        project: 'Silver Spring / New Carrollton, MD',
        cause:
          'A $106,586 shortfall (guaranteed $2,701,713 against actual $2,595,127) paid in full without comparing utility bills; more than $2M in O&M savings guaranteed but the O&M contract was never modified and the base contract could not be produced; lamp and ballast savings not netted against the roughly $32,000 already budgeted.',
        amount: '$80.5M task order · ~$704k netting exposure',
        failureModeIds: ['FM-05', 'FM-06', 'FM-12'],
      },
      {
        project: 'Los Angeles, CA',
        cause:
          "GSA's energy usage database showed more than $539,000 of shortfall over three years while the ESCO's M&V reports for the same period reported the guarantee met; PBS paid in full and could not explain why it never researched the discrepancy. Separately, $69,000/yr claimed from hand dryers reducing paper towel use, with occupants given both options and no towel-usage analysis performed.",
        amount: '$63.2M task order · >$539,000 unreconciled',
        failureModeIds: ['FM-12', 'FM-07'],
      },
      {
        project: 'St. Louis, MO',
        cause:
          'Post-installation witnessing performed on 1 of 12 ECMs; the Year 1 M&V review found deficiencies and the ESCO was paid in full anyway.',
        amount: '$15.7M task order',
        failureModeIds: ['FM-02'],
      },
      {
        project: 'St. Croix, VI',
        cause: 'Witnessing and contract file findings.',
        amount: '$12.5M task order',
        failureModeIds: ['FM-02'],
      },
      {
        project: 'Battle Creek, MI',
        cause: 'Witnessing findings.',
        amount: '$13.2M task order',
        failureModeIds: ['FM-02'],
      },
      {
        project: 'San Juan, PR / St. Thomas, VI',
        cause:
          'Project team relied on ESCO-prepared appendix measurements with no independent PBS review.',
        amount: '$21.8M task order',
        failureModeIds: ['FM-02', 'FM-03'],
      },
    ],
  },

  'GSA-2025': {
    id: 'GSA-2025',
    shortLabel: 'GSA OIG A240046-1',
    issuer: 'GSA Office of Inspector General',
    reportNumber: 'A240046-1',
    date: '2025-07-01',
    url: 'https://www.gsaig.gov/sites/default/files/audit-reports/A240046-1%20Final%20Report.pdf',
    scale: '10 buildings · TX and LA',
    summary:
      'Ten buildings across Texas and Louisiana. Finding 2 is the one to lead with: a dollar-attached instance of the conflict-of-interest structure in the witness role.',
    incidents: [
      {
        project: '10 buildings, TX and LA',
        cause:
          'Baseline measurements not witnessed or verified, so PBS negotiated the guarantee off contractor data alone.',
        failureModeIds: ['FM-02'],
      },
      {
        project: '10 buildings, TX and LA',
        cause:
          'O&M contractor employees served as government witnesses and signed the baseline witnessing forms. The OIG recommended PBS adhere to policy to ensure O&M contractor employees do not serve as government witnesses.',
        failureModeIds: ['FM-03'],
      },
      {
        project: 'The Centre Phase 5, Farmers Branch, TX',
        cause:
          'Window inserts installed after they were no longer needed, because no modification was issued to stop them. Separately, a PBS employee authorized a transformer scope change with no contracting officer modification.',
        amount: '$71,920.47 unnecessary cost',
        failureModeIds: ['FM-11'],
      },
    ],
  },

  'VA-2023': {
    id: 'VA-2023',
    shortLabel: 'VA OIG 22-02934-208',
    issuer: 'VA Office of Inspector General',
    reportNumber: '22-02934-208',
    date: '2023-12-14',
    url: 'https://www.vaoig.gov/sites/default/files/reports/2023-12/vaoig-22-02934-208.pdf',
    scale: '4 facilities · $68M dispute',
    summary:
      'Four randomly selected medical facilities out of thirteen — Beckley WV, Fayetteville NC, Little Rock AR, Palo Alto CA — under contracts VA701-16-F-0067 and VA701-14-F-0116. Contains the corpus’s best backtest case: two competent parties reading one clause two ways, with both readings printed.',
    incidents: [
      {
        project: 'All four facilities',
        cause:
          'Baselines never witnessed. The energy managers who should have witnessed had left VA and their successors could produce nothing; one witnessing form was signed in April 2022, long after the measure was installed.',
        failureModeIds: ['FM-02'],
      },
      {
        project: 'McClellan (Little Rock, AR)',
        cause:
          'Interior lighting. Room-by-room fixture inventory, operating hours sampled with time-of-use loggers, averaged by room type and projected facility-wide, with no witnessing of the logger measurements. The claim was 37% of the contract’s total annual savings.',
        amount: '$2.1M measure · $168,126/yr claimed',
        failureModeIds: ['FM-01', 'FM-08'],
      },
      {
        project: 'Fayetteville, NC',
        cause:
          'Low-flow plumbing fixtures resting on assumed faucet uses per year, water per occurrence, and hot-water share. The entire documented VA review of that ECM was one comment about a rounding error.',
        amount: '$35,501/yr claimed · 5.5-year payback',
        failureModeIds: ['FM-08'],
      },
      {
        project: 'Palo Alto, CA',
        cause:
          'The comment log contained two questions across six ECMs, covering measures worth roughly 37% of total savings, and does not record whether the contractor’s answers were accepted.',
        failureModeIds: ['FM-08'],
      },
      {
        project: 'Prepayment dispute (VA formally non-concurred)',
        cause:
          'VA OGC, consulting DOE OGC, read EISA 2007’s Funding Options language as authorizing the payments; the OIG read 42 U.S.C. §8287(a)(2)(B) as still capping aggregate annual payments. The disagreement turned on whether Appendix 2 of a January 2009 FEMP guide was superseded by Appendix 3.',
        amount: '$68M',
        failureModeIds: ['FM-04'],
      },
    ],
  },

  'FEMP-AUDIT-2024': {
    id: 'FEMP-AUDIT-2024',
    shortLabel: 'FEMP ESPC audit guide 2024',
    issuer: 'DOE Federal Energy Management Program',
    reportNumber: null,
    date: '2024-02',
    url: 'https://www.osti.gov/servlets/purl/2477296',
    scale: 'Synthesis of 30 GAO and OIG audits',
    summary:
      'Resource for Auditing Energy Saving Performance Contracts. Not a dispute but the meta-source, built from a review of 30 GAO and OIG audits across GSA, DOE and DoD. Section 4 enumerates the recurring findings; the taxonomy is built on its spine.',
    incidents: [
      {
        project: 'Version drift, in FEMP’s own voice',
        cause:
          'Contract language reading "shall comply with the latest version in effect at the time of task order award" requires knowing when the project was awarded and what was in force then. Auditors are told to contact FEMP if they cannot determine it.',
        failureModeIds: ['FM-04'],
      },
      {
        project: 'IDIQ generation change',
        cause:
          'The witnessing obligation changed between contract generations. Gen 2 (Dec 2008) §C.4.6 says the agency should witness; Gen 3 (Apr 2017) and Gen 4 (Aug 2023) make it required. Identical plan text therefore carries a different obligation depending on award date.',
        failureModeIds: ['FM-02'],
      },
    ],
  },

  'FEMP-MV-5': {
    id: 'FEMP-MV-5',
    shortLabel: 'FEMP M&V Guidelines v5.0',
    issuer: 'DOE Federal Energy Management Program',
    reportNumber: null,
    date: '2024-02',
    url: 'https://www.energy.gov/sites/default/files/2024-03/femp-mv-5-0-guidelines_DRAFT.pdf',
    scale: 'M&V Guidelines v5.0 — the rubric',
    summary:
      'The standard, not a plan. Table 3-1 is the RRP Matrix: 16 risk rows across Financial, Operational and Performance, each with FEMP’s own "clarify X" instruction. §2.2.4 lists required plan contents, §4.2.1.2 gives permitted and prohibited sources for estimated parameters, and §2.1.4 requires the plan to list static factors and the thresholds that trigger a non-routine adjustment.',
    incidents: [
      {
        project: '§4.2.1.2 — prohibited sources',
        cause:
          'Rules of thumb, black-box algorithms, handshake agreements, guesses at operating parameters, and equations derived from questionable data are named as impermissible sources for estimated parameters.',
        failureModeIds: ['FM-08'],
      },
      {
        project: '§2.1.4 — non-routine adjustments',
        cause:
          'The plan should list all the possible static factors that might affect an ECM’s savings, and the thresholds for what triggers a non-routine adjustment.',
        failureModeIds: ['FM-09'],
      },
      {
        project: 'FEMP Example M&V Plan §5.7',
        cause:
          'A 600,000 ton-hour floor on claimed savings, and uncapped replacement of missing intervals by interpolation or average values, are both present in the government’s own model document.',
        failureModeIds: ['FM-10', 'FM-13'],
      },
      {
        project: 'FEMP Example M&V Plan §3.1',
        cause:
          'Lighting interactive effects are excluded while chiller savings are computed from measured tons — an asymmetric exclusion within one plan.',
        failureModeIds: ['FM-14'],
      },
    ],
  },
};

export const EVIDENCE_CASE_LIST: EvidenceCase[] = Object.values(EVIDENCE_CASES);

/** The three federal audits, for the landing page evidence strip. */
export const FEDERAL_AUDITS: EvidenceCase[] = [
  EVIDENCE_CASES['GSA-2020'],
  EVIDENCE_CASES['GSA-2025'],
  EVIDENCE_CASES['VA-2023'],
];

/** "2020-03-27" -> "27 Mar 2020"; "2024-02" -> "Feb 2024". */
export function formatCaseDate(iso: string): string {
  const MONTHS = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  const [y, m, d] = iso.split('-');
  const month = MONTHS[Number(m) - 1] ?? m;
  return d ? `${Number(d)} ${month} ${y}` : `${month} ${y}`;
}
